import NavbarV2 from '../../components/navbarV2/NavbarV2';
import './CreditNotesV2.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaFileAlt } from 'react-icons/fa';
import CreditNotesList from '../../components/CreditNotesList/CreditNotesList';
import ListEmptyStateV2, { ListPageLoadingV2 } from '../../components/listEmptyStateV2/ListEmptyStateV2';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';

const CreditNotesV2 = () => {
    const [orders, setOrders] = useState([]);
    const [initialFetchDone, setInitialFetchDone] = useState(false);
    const [page, setPage] = useState(1);
    const [query, setQuery] = useState('');
    const navigate = useNavigate();
    const prevSearch = useRef(null);
    const isAdmin = localStorage.getItem('admin') === 'true';

    const [debouncedQuery, flushQuery] = useDebouncedValue(query, 350);

    const loadOrders = useCallback(
        (index, searchQ) => {
            let url = `${process.env.REACT_APP_API}/api/getAllOrdersWithCreditNotes`;
            const params = new URLSearchParams();
            if (searchQ.length > 0) params.set('query', searchQ);
            params.set('page', String(index));
            url += `?${params.toString()}`;

            fetch(url, {})
                .then(async (response) => {
                    if (response.status === 200) {
                        if (localStorage.getItem('account') !== null) {
                            const data = await response.json();
                            setOrders(data);
                        } else {
                            navigate('/');
                        }
                    }
                })
                .catch(() => {})
                .finally(() => setInitialFetchDone(true));
        },
        [navigate],
    );

    useEffect(() => {
        if (!prevSearch.current) {
            prevSearch.current = { q: debouncedQuery };
            loadOrders(page, debouncedQuery);
            return;
        }
        const searchChanged = prevSearch.current.q !== debouncedQuery;

        if (searchChanged) {
            prevSearch.current = { q: debouncedQuery };
            if (page !== 1) {
                setPage(1);
            } else {
                loadOrders(1, debouncedQuery);
            }
            return;
        }

        loadOrders(page, debouncedQuery);
    }, [page, debouncedQuery, loadOrders]);

    const runSearchNow = () => {
        flushQuery();
        setPage(1);
    };

    const clearSearch = () => {
        setQuery('');
        flushQuery();
        setPage(1);
    };

    const hasActiveSearch = Boolean(debouncedQuery);

    return (
        <div className="ordersv2">
            <NavbarV2 />

            <div className="ordersv2-header">
                <h1 className="ordersv2-title">Credit notes</h1>
            </div>

            {isAdmin && (
                <div className="ordersv2-filters">
                    <div className="ordersv2-field">
                        <label>Search</label>
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Credit note #, order #, invoice #, name, email, phone…"
                        />
                    </div>
                    <div className="ordersv2-filter-actions">
                        <button type="button" className="ordersv2-btn ordersv2-btn--primary" onClick={runSearchNow}>
                            Search now
                        </button>
                    </div>
                </div>
            )}

            {isAdmin && (
                <div className="ordersv2-pagination">
                    <button
                        type="button"
                        className="ordersv2-page-btn"
                        onClick={() => page > 1 && setPage(page - 1)}
                        disabled={page <= 1}
                    >
                        <FaChevronLeft size={10} /> Previous
                    </button>
                    <p className="ordersv2-page-num">{page}</p>
                    <button type="button" className="ordersv2-page-btn" onClick={() => setPage(page + 1)}>
                        Next <FaChevronRight size={10} />
                    </button>
                </div>
            )}

            {!initialFetchDone && <ListPageLoadingV2 />}
            {initialFetchDone && orders.length === 0 && isAdmin && hasActiveSearch && (
                <ListEmptyStateV2
                    icon={FaFileAlt}
                    title="No credit notes found"
                    description="No orders with credit notes matched this search. Try different keywords or reset the search."
                    secondaryAction={{ label: 'Clear search', onClick: clearSearch }}
                />
            )}
            {initialFetchDone && orders.length === 0 && !(isAdmin && hasActiveSearch) && (
                <ListEmptyStateV2
                    icon={FaFileAlt}
                    title="No credit notes"
                    description={
                        isAdmin
                            ? 'When credit notes are issued against orders, they will appear in this list.'
                            : 'You do not have any credit notes on your account yet.'
                    }
                    primaryAction={!isAdmin ? { to: '/', label: 'Browse products' } : undefined}
                />
            )}
            {orders.length > 0 && <CreditNotesList data={orders} />}
        </div>
    );
};

export default CreditNotesV2;
